<template>
  <footer
    class="flex flex-wrap gap-7 justify-center mt-14 items-center self-stretch py-4 w-full text-base font-medium whitespace-nowrap border-t border-gray-300 border-solid max-md:max-w-full"
  >
    <div class="flex w-1/2 gap-4 justify-center items-center">
      <BaseButton
        type="button"
        class="flex overflow-hidden w-1/4 flex-wrap gap-3 justify-center items-center self-stretch py-2.5 pr-4 pl-4 my-auto text-white bg-violet-600 rounded-md shadow-sm max-md:max-w-full"
        @click="next"
        :disabled="localIsDisabled"
      >
        <span v-if="!localIsDisabled" class="self-stretch my-auto">
          Next
          <i class="fas fa-arrow-right shrink-0 w-5 aspect-square"></i>
        </span>
        <span v-else class="self-stretch my-auto">Submitting.....</span>
      </BaseButton>
    </div>
  </footer>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, PropType, ref, watch } from "vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import { ApolloError, gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import { PostHog } from "posthog-js";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { scrollToTop } from "@/utils/useCommon";

const UPDATE_PROGRAM = gql`
  mutation updateProgram($input: UpdateProgramInput!) {
    updateProgram(input: $input) {
      program {
        id
      }
      errors
    }
  }
`;

export default defineComponent({
  name: "FooterSection",
  components: {
    BaseButton,
  },
  props: {
    isDisabled: {
      type: Boolean as PropType<boolean>,
      required: false,
    },
    backButtonVisible: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    currentView: {
      type: String as PropType<string>,
      default: "",
    },
    setErrorMessage: {
      type: Function as PropType<(message: string) => void>,
      required: false,
    },
  },

  emits: ["back", "next"],
  setup(props, { emit }) {
    const localIsDisabled = ref<boolean>(props.isDisabled || false);
    const userStore = useUserStore();
    const router = useRouter();
    const posthog = (getCurrentInstance()?.proxy as { $posthog: PostHog })
      ?.$posthog;

    const {
      mutate: updateProgram,
      onDone: updateProgramDone,
      onError: updateProgramError,
    } = useMutation(UPDATE_PROGRAM);

    const next = () => {
      if (props.currentView && props.currentView == "add-integration") {
        props.setErrorMessage?.("");
        callUpdateProgram();
      } else {
        emit("next");
      }
    };

    const callUpdateProgram = () => {
      localIsDisabled.value = true;

      updateProgram({
        input: {
          programParams: {
            id: userStore.createExpertProgram?.id,
            onboardingStep: "completed",
          },
          claimProgram: false,
        },
      });

      updateProgramDone((response) => {
        if (response) {
          localIsDisabled.value = false;
          if (response?.errors && response?.errors?.length > 0) {
            props.setErrorMessage?.(response.errors[0].message);
          } else {
            if (response?.data?.updateProgram?.errors?.length > 0) {
              props.setErrorMessage?.(
                response.data.updateProgram.errors.join(".")
              );
              scrollToTop();
            } else {
              handleSuccess();
            }
          }
        }
      });
      updateProgramError((error: ApolloError) => {
        localIsDisabled.value = false;
        props.setErrorMessage?.(error.message);
        scrollToTop();
      });
    };

    const handleSuccess = () => {
      notifyPosthog();
      userStore.clearUpdateExpertProgram();
      router.push({ name: "ExpertPrograms" });
    };

    const notifyPosthog = () => {
      if (posthog) {
        posthog.capture("Company_User_Completes_Sign_Up", {
          email: userStore.email,
          company_name: userStore.companyName,
          program_name: userStore.createExpertProgram?.name,
        });
      }
    };

    watch(
      () => props.isDisabled,
      (newValue) => {
        localIsDisabled.value = newValue ?? false;
      },
      { immediate: true }
    );

    return {
      next,
      localIsDisabled,
    };
  },
});
</script>
